import { useParams, useNavigate } from "react-router-dom";
import { useState, useEffect, useRef, type ChangeEvent } from "react";
import { useScrollRestoration } from "../hooks/useScrollRestoration";
import { useProduct } from "../hooks/useProduct";
import { AssetSettingsForm } from "../components/Products/AssetSettingsForm";
import { ModelViewer } from "../components/Products/ModelViewer";
import { AssetStatusBadge } from "../components/Products/AssetStatusBadge";
import { GenerationProgress } from "../components/Products/GenerationProgress";
import { ImagePicker } from "../components/Products/ImagePicker";
import { useToast } from "../hooks/useToast";
import { extractErrorMessage, toErrorMessage } from "../utils/errorMessage";
import { Skeleton, SkeletonText } from "../components/ui/Skeleton";
import { QuotaCapModal } from "../components/Billing/QuotaCapModal";
import { useBilling } from "../hooks/useBilling";
import { getApiClient } from "../api/client";
import { getShopId } from "../utils/shopId";
import { assetEditorApi, generationsApi, productsApi } from "../api/products.api";
import { useSettings } from "../hooks/useSettings";
import type { GenerationRunResponseV2, OrientationOverride } from "../types/product.types";
import { AssetEditor } from "../components/Products/AssetEditor";

export function ProductDetailPage() {
  useScrollRestoration();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { product, loading, error, updateSettings, cancelGeneration, deleteAsset, refetch } = useProduct(id || "");
  const { showError, showSuccess } = useToast();
  const { plan } = useBilling();
  const [isGenerating, setIsGenerating] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [viewMode, setViewMode] = useState<'single' | 'multi'>('single');
  const [activeRunId, setActiveRunId] = useState<string | null>(null);
  const [activeRunStatus, setActiveRunStatus] = useState<GenerationRunResponseV2 | null>(null);
  const [generationStartedAt, setGenerationStartedAt] = useState<string | null>(null);
  const [activeGenerationMode, setActiveGenerationMode] = useState<string | null>(null);

  // Prefill capture/quality mode from shop settings (one-time, on load)
  const { settings: shopSettings } = useSettings();
  const defaultsAppliedRef = useRef(false);
  useEffect(() => {
    if (shopSettings && !defaultsAppliedRef.current) {
      defaultsAppliedRef.current = true;
      setViewMode(shopSettings.default_capture_mode === 'multi_angle_capture' ? 'multi' : 'single');
    }
  }, [shopSettings]);
  const [showQualitySelector, setShowQualitySelector] = useState(false);
  const [showQuotaModal, setShowQuotaModal] = useState(false);
  const [activeDetailTab, setActiveDetailTab] = useState<'generation' | 'edit-asset'>('generation');
  const [selectedImages, setSelectedImages] = useState<string[]>([]);
  const [allImages, setAllImages] = useState<string[]>([]);
  const [imagesFetched, setImagesFetched] = useState(false);
  const [previousAssetStatus, setPreviousAssetStatus] = useState<string | null>(null);
  const [glbProxyUrl, setGlbProxyUrl] = useState<string | null>(null);
  const [glbRefreshKey, setGlbRefreshKey] = useState(0);
  const glbBlobUrlRef = useRef<string | null>(null);
  const [editorOrientation, setEditorOrientation] = useState<OrientationOverride | null>(null);
  const [customGlbUploading, setCustomGlbUploading] = useState(false);
  const customGlbInputRef = useRef<HTMLInputElement>(null);

  async function handleCustomGlbSelected(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !product?.id) return;
    if (!file.name.toLowerCase().endsWith(".glb")) {
      showError("Please choose a .glb file.");
      return;
    }
    setCustomGlbUploading(true);
    try {
      await assetEditorApi.uploadCustomGlb(product.id, file);
      await refetch(true);
      setGlbRefreshKey((k) => k + 1);
      showSuccess("Custom 3D model uploaded. You can adjust it under Edit Asset.");
    } catch (err: unknown) {
      showError(extractErrorMessage(err, "Upload failed. Use a valid binary GLB."));
    } finally {
      setCustomGlbUploading(false);
    }
  }

  // Fetch GLB via proxy (bypasses CORS/401 on direct R2 URL) and create blob URL for model-viewer
  useEffect(() => {
    if (!product?.id || !product?.asset?.id || product.asset.status !== "ready") {
      setGlbProxyUrl(null);
      return;
    }
    const client = getApiClient();
    const params = getShopId() ? { shop_id: getShopId() } : undefined;
    client
      .get(`/api/products/${product.id}/assets/${product.asset.id}/glb`, {
        responseType: "arraybuffer",
        params: { ...params, _cb: glbRefreshKey },
      })
      .then((res) => {
        if (glbBlobUrlRef.current) URL.revokeObjectURL(glbBlobUrlRef.current);
        const blob = new Blob([res.data], { type: "model/gltf-binary" });
        const url = URL.createObjectURL(blob);
        glbBlobUrlRef.current = url;
        setGlbProxyUrl(url);
      })
      .catch(() => setGlbProxyUrl(null));

    return () => {
      if (glbBlobUrlRef.current) {
        URL.revokeObjectURL(glbBlobUrlRef.current);
        glbBlobUrlRef.current = null;
      }
      setGlbProxyUrl(null);
    };
  }, [product?.id, product?.asset?.id, product?.asset?.status, product?.asset?.glb_url, glbRefreshKey]);

  // Fetch the editor's orientationOverride so the 3D preview reflects the same
  // rotation the user configured in the Edit Asset panel.
  useEffect(() => {
    if (!product?.id || product?.asset?.status !== "ready") {
      setEditorOrientation(null);
      return;
    }
    assetEditorApi.getState(product.id)
      .then((state) => {
        setEditorOrientation(state.orientationOverride ?? null);
      })
      .catch(() => setEditorOrientation(null));
  }, [product?.id, product?.asset?.status, glbRefreshKey]);

  // Resolve all images for the product. Use cached image_urls if present,
  // otherwise fetch from Shopify via the backend (lazy, one-time call).
  useEffect(() => {
    if (!product || imagesFetched) return;

    const cached = product.image_urls ?? (product.primary_image_url ? [product.primary_image_url] : []);

    if (cached.length > 1) {
      // DB already has all images — default selection starts from the first image.
      setAllImages(cached);
      setSelectedImages((prev) =>
        prev.length > 0 ? prev : [cached[0]].filter(Boolean)
      );
      setImagesFetched(true);
    } else {
      // Fetch fresh list from Shopify (populates DB for next time)
      productsApi.getImages(product.id)
        .then((imgs) => {
          const resolved = imgs.length > 0 ? imgs : cached;
          setAllImages(resolved);
          setSelectedImages((prev) =>
            prev.length > 0 ? prev : [resolved[0]].filter(Boolean)
          );
        })
        .catch(() => {
          // Network failure: fall back to whatever we have locally
          setAllImages(cached);
          setSelectedImages((prev) =>
            prev.length > 0 ? prev : [cached[0]].filter(Boolean)
          );
        })
        .finally(() => setImagesFetched(true));
    }
  }, [product, imagesFetched]);

  // When viewMode is multi, normalize selectedImages to 4 slots ('' for empty)
  useEffect(() => {
    if (viewMode !== 'multi') return;
    setSelectedImages((prev) => {
      const filled = prev.filter(Boolean);
      // If we already have 4 non-empty images, keep as-is
      if (filled.length >= 4) return filled.slice(0, 4);
      // Fill remaining slots from allImages
      const usedSet = new Set(filled);
      const extras = allImages.filter((img) => !usedSet.has(img));
      const slots: string[] = [...filled];
      for (const img of extras) {
        if (slots.length >= 4) break;
        slots.push(img);
      }
      while (slots.length < 4) slots.push('');
      return slots.slice(0, 4);
    });
  }, [viewMode, allImages]);

  // Monitor asset status changes and show success message when ready
  useEffect(() => {
    const currentStatus = product?.asset?.status;
    
    // If asset just became ready, show success message and reset generating state
    if (
      currentStatus === 'ready' &&
      previousAssetStatus &&
      previousAssetStatus !== 'ready' &&
      !activeRunId
    ) {
      setIsGenerating(false);
      setActiveRunStatus(null);
      setGenerationStartedAt(null);
      showSuccess("AR model generation completed successfully! The 3D preview is now available.");
    }
    
    // Update previous status
    if (currentStatus) {
      setPreviousAssetStatus(currentStatus);
    }
  }, [activeRunId, product?.asset?.status, previousAssetStatus, showSuccess]);

  useEffect(() => {
    if (!activeRunId) return;

    let cancelled = false;
    const intervalMs = 4000;
    let consecutiveErrors = 0;
    const MAX_CONSECUTIVE_ERRORS = 5;

    const pollRun = async () => {
      if (cancelled) return;
      try {
        const run = await generationsApi.get(activeRunId);
        consecutiveErrors = 0; // reset on success
        setActiveRunStatus(run);
        if (!generationStartedAt) {
          setGenerationStartedAt(run.created_at || new Date().toISOString());
        }

        if (run.status === "completed") {
          setActiveRunId(null);
          await generationsApi.promoteMaster(run.run_id);
          await refetch(true);
          if (!cancelled) {
            setIsGenerating(false);
            setActiveRunStatus(null);
            setGenerationStartedAt(null);
            setActiveGenerationMode(null);
            showSuccess("AR model generation completed successfully! The 3D preview is now available.");
          }
          return;
        }

        if (run.status === "failed" || run.status === "cancelled") {
          setActiveRunId(null);
          if (!cancelled) {
            setIsGenerating(false);
            setActiveRunStatus(run);
            setActiveGenerationMode(null);
            showError(
            typeof run.error === 'string'
              ? run.error
              : typeof run.message === 'string'
                ? run.message
                : `Generation ${run.status}.`
          );
          }
        }
      } catch (err: any) {
        consecutiveErrors++;
        // Only stop polling after too many consecutive failures — transient network
        // errors (e.g. brief internet drop) should not stop an active generation.
        if (!cancelled && consecutiveErrors >= MAX_CONSECUTIVE_ERRORS) {
          setActiveRunId(null);
          setIsGenerating(false);
          setActiveRunStatus(null);
          showError(extractErrorMessage(err, "Lost connection to server. Reload the page to check generation status."));
        }
        // Otherwise silently continue polling — the generation is still running
      }
    };

    pollRun();
    const timer = setInterval(pollRun, intervalMs);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [activeRunId, generationStartedAt, refetch, showError, showSuccess]);

  const handleGenerateClick = () => {
    // Check if at quota cap before showing selector
    const usage = plan?.usage;
    const planCode = plan?.plan?.code;
    if (
      usage &&
      planCode !== 'scale' &&
      planCode !== 'admin' &&
      usage.max_assets_per_month > 0 &&
      usage.assets_generated_this_month >= usage.max_assets_per_month
    ) {
      setShowQuotaModal(true);
      return;
    }
    setShowQualitySelector(true);
  };

  const handleGenerateConfirm = async () => {
    if (!product) {
      return;
    }

    const generationMode = viewMode === "multi" ? "multi_ultra" : "single_ultra";
    const imagesToUse =
      generationMode === "single_ultra"
        ? [selectedImages[0] ?? allImages[0]].filter(Boolean)
        : selectedImages.filter(Boolean).slice(0, 4);

    if (imagesToUse.length === 0) {
      showError("Please select at least one image.");
      return;
    }

    setIsGenerating(true);
    setShowQualitySelector(false);
    setActiveGenerationMode(generationMode);

    // Auto-detect flat artwork products by product_type so the orchestrator can
    // generate a flat plane GLB instead of a distorted 3D mesh.
    const FLAT_PRODUCT_TYPES = new Set([
      'gift card', 'gift_card', 'giftcard',
      'artwork', 'art', 'painting', 'poster', 'print', 'canvas',
      'photograph', 'photo', 'card', 'greeting card',
    ]);
    const rawType = (product.product_type ?? '').toLowerCase().trim();
    const detectedCategory = FLAT_PRODUCT_TYPES.has(rawType) ? rawType.replace(/\s+/g, '_') : undefined;

    try {
      const response = await generationsApi.create({
        product_id: product.id,
        mode: generationMode,
        source_images: imagesToUse,
        requested_dims: product.asset
          ? {
              w: product.asset.scale_x,
              h: product.asset.scale_y,
              d: product.asset.scale_z,
            }
          : undefined,
        category: detectedCategory,
      });

      setActiveRunId(response.run_id);
      setActiveRunStatus(response);
      setGenerationStartedAt(response.created_at || new Date().toISOString());
      showSuccess(
        generationMode === "multi_ultra"
          ? "Multi-View Ultra generation started. This may take a few minutes."
          : "Single Reference Ultra generation started. This may take a few minutes."
      );
    } catch (err: any) {
      console.error("Failed to generate asset:", err);
      showError(extractErrorMessage(err, "Failed to generate AR model. Please try again."));
      setIsGenerating(false);
      setActiveRunStatus(null);
    }
  };

  const handleGenerateCancel = () => {
    setShowQualitySelector(false);
  };

  const handleCancel = async () => {
    if (!confirm("Are you sure you want to cancel the generation? This cannot be undone.")) {
      return;
    }
    setIsCancelling(true);
    try {
      if (activeRunId) {
        await generationsApi.cancel(activeRunId);
        setActiveRunId(null);
        setActiveRunStatus(null);
        setActiveGenerationMode(null);
      } else {
        await cancelGeneration();
      }
      setIsGenerating(false);
      setGenerationStartedAt(null);
      await refetch(true);
      showSuccess("Generation cancelled successfully.");
    } catch (err: any) {
      console.error("Failed to cancel generation:", err);
      showError(extractErrorMessage(err, "Failed to cancel generation. Please try again."));
    } finally {
      setIsCancelling(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm("Are you sure you want to delete this AR asset? This cannot be undone.")) {
      return;
    }
    setIsDeleting(true);
    try {
      await deleteAsset();
      showSuccess("AR asset deleted successfully.");
    } catch (err: any) {
      console.error("Failed to delete asset:", err);
      showError(extractErrorMessage(err, "Failed to delete asset. Please try again."));
    } finally {
      setIsDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="page-grid">
        <Skeleton variant="rectangular" width={100} height={32} className="rounded-2xl" />
        <div className="glass-panel">
          <div className="grid gap-6 lg:grid-cols-2">
            <Skeleton variant="rectangular" width="100%" height={400} className="rounded-3xl" />
            <div className="space-y-4">
              <Skeleton variant="rectangular" width={120} height={32} className="rounded-2xl" />
              <Skeleton variant="text" width="80%" className="h-8" />
              <SkeletonText lines={3} />
              <Skeleton variant="rectangular" width={180} height={44} className="rounded-2xl" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="glass-panel text-sm text-rose-500">
        {error ? toErrorMessage(error, "Product not found") : "Product not found"}
      </div>
    );
  }

  const generationInFlight =
    product.asset?.status === "pending" || product.asset?.status === "processing";
  const hasReadyGlbModel = product.asset?.status === "ready" && !!product.asset.glb_url;
  const canUploadCustomGlb = !generationInFlight;

  return (
    <div className="page-grid">
      <button
        type="button"
        onClick={() => navigate("/products")}
        className="inline-flex items-center gap-2 text-sm font-semibold text-slate-500 hover:text-slate-900"
      >
        <span aria-hidden="true">&larr;</span>
        Back to products
      </button>

      <div
        className={`flex flex-wrap items-start gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-sm ${!canUploadCustomGlb ? "opacity-80" : ""}`}
      >
        <div className="min-w-0 flex-1 space-y-1">
          <p className="text-xs leading-relaxed text-slate-600">
            <span className="font-semibold text-slate-800">Bring your own 3D model.</span>{" "}
            {hasReadyGlbModel
              ? "Optional: upload a GLB you created in another tool. It replaces the current file; you can still edit materials and orientation under Edit Asset."
              : "Optional: upload a binary .glb to attach a 3D model without running AI generation first. Configure placement and AR under Generation Settings after upload."}
          </p>
          {generationInFlight && (
            <p className="text-xs font-medium text-amber-700">
              Upload is disabled while a 3D generation is running. Cancel or wait for it to finish.
            </p>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <input
            ref={customGlbInputRef}
            type="file"
            accept=".glb,model/gltf-binary"
            className="hidden"
            disabled={!canUploadCustomGlb || customGlbUploading}
            onChange={handleCustomGlbSelected}
          />
          <button
            type="button"
            disabled={!canUploadCustomGlb || customGlbUploading}
            onClick={() => customGlbInputRef.current?.click()}
            className="rounded-xl border border-slate-300 bg-slate-50 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-white disabled:cursor-not-allowed disabled:opacity-50"
          >
            {customGlbUploading ? "Uploading…" : "Upload GLB"}
          </button>
        </div>
      </div>

      <div className="glass-panel">
        <div className="grid gap-6 lg:grid-cols-2">
          <div className="space-y-4">
            {/* Primary/selected image preview */}
            {(selectedImages[0] ?? product.primary_image_url) && (
              <img
                src={selectedImages[0] ?? product.primary_image_url!}
                alt={product.title}
                className="w-full rounded-3xl border border-slate-100 object-cover"
              />
            )}
            {/* View mode toggle */}
            {allImages.length > 1 && (
              <div className="space-y-2">
                <p className="text-sm font-semibold text-slate-700">Generation mode</p>
                <div className="flex gap-2">
                  <label
                    className={`cursor-pointer rounded-2xl border px-4 py-2 text-sm font-semibold transition ${
                      viewMode === 'single'
                        ? 'border-indigo-400 bg-indigo-50 text-indigo-700'
                        : 'border-slate-200 bg-white text-slate-500 hover:border-slate-300'
                    }`}
                  >
                    <input
                      type="radio"
                      name="viewMode"
                      value="single"
                      checked={viewMode === 'single'}
                      onChange={() => setViewMode('single')}
                      className="sr-only"
                    />
                    Single Reference Ultra
                  </label>
                  <label
                    className={`cursor-pointer rounded-2xl border px-4 py-2 text-sm font-semibold transition ${
                      viewMode === 'multi'
                        ? 'border-indigo-400 bg-indigo-50 text-indigo-700'
                        : 'border-slate-200 bg-white text-slate-500 hover:border-slate-300'
                    }`}
                  >
                    <input
                      type="radio"
                      name="viewMode"
                      value="multi"
                      checked={viewMode === 'multi'}
                      onChange={() => setViewMode('multi')}
                      className="sr-only"
                    />
                    Multi-View Ultra
                  </label>
                </div>
                {viewMode === 'multi' && selectedImages.filter(Boolean).length < 1 && allImages.length > 0 && (
                  <p className="text-xs text-amber-600">
                    Select at least 1 photo for Multi-View generation.
                  </p>
                )}
              </div>
            )}
            {/* Image picker — shown when we have 2+ images */}
            {allImages.length > 1 && (
              <ImagePicker
                images={allImages}
                selectedImages={selectedImages}
                onChange={setSelectedImages}
                viewLabels={viewMode === 'multi'}
                maxImages={viewMode === 'single' ? 1 : 4}
                viewMode={viewMode}
                strictMultiSlots={viewMode === 'multi'}
                disabled={isGenerating || product.asset?.status === "processing" || product.asset?.status === "pending"}
              />
            )}
            <div className="space-y-2 text-sm text-slate-500">
              <p>
                <span className="font-semibold text-slate-900">Type:</span> {product.product_type || "N/A"}
              </p>
              <p>
                <span className="font-semibold text-slate-900">Status:</span> {product.status}
              </p>
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-3">
              <AssetStatusBadge status={(product.asset?.status || "none") as any} />
              {product.asset?.error_message && (
                <span className="rounded-2xl border border-rose-100 bg-rose-50/60 px-3 py-1 text-xs font-semibold text-rose-600">
                  {product.asset.error_message}
                </span>
              )}
            </div>
            <h1 className="text-2xl font-semibold text-slate-900">{product.title}</h1>
            <p className="text-sm text-slate-500">{product.body_html || "No description"}</p>
          <div className="flex flex-wrap items-center gap-3">
          {!isGenerating && !showQualitySelector &&
           product.asset?.status !== "processing" && 
           product.asset?.status !== "pending" ? (
            <button
              type="button"
              onClick={handleGenerateClick}
              disabled={
                isGenerating ||
                (viewMode === 'single' && !selectedImages[0] && !allImages[0]) ||
                (viewMode === 'multi' && selectedImages.filter(Boolean).length === 0 && allImages.length === 0)
              }
              className="rounded-2xl bg-gradient-to-r from-indigo-500 to-sky-500 px-6 py-3 text-sm font-semibold text-white shadow-xl shadow-indigo-200 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {product.asset?.status === 'ready' ? 'Regenerate AR model' : 'Generate AR model'}
            </button>
          ) : (isGenerating || product.asset?.status === "processing" || product.asset?.status === "pending") ? (
            <GenerationProgress 
              productTitle={product.title}
              startedAt={generationStartedAt || activeRunStatus?.created_at || product.asset?.generation_started_at}
              progressMessage={
                activeRunStatus?.logs && activeRunStatus.logs.length > 0
                  ? activeRunStatus.logs[activeRunStatus.logs.length - 1]?.message
                  : product.asset?.generation_progress_message
              }
              currentStage={activeRunStatus?.current_stage || null}
              stageLogs={activeRunStatus?.logs || []}
              onCancel={handleCancel}
              showCancel={!isCancelling && (isGenerating || product.asset?.status === "processing" || product.asset?.status === "pending")}
              cancelButtonText="Cancel generation"
              embedded={true}
              mode={activeGenerationMode}
            />
          ) : null}
          {product.asset?.status === "ready" && !isDeleting && !isGenerating && !showQualitySelector && (
            <button
              type="button"
              onClick={handleDelete}
              disabled={isDeleting}
              className="rounded-2xl border-2 border-rose-200 bg-white px-6 py-3 text-sm font-semibold text-rose-600 hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-60"
            >
              Delete Asset
            </button>
          )}
          </div>
          </div>
        </div>
      </div>

      {product.asset?.glb_url && product.asset.status === "ready" && (
        <div className="glass-panel">
          <h2 className="text-lg font-semibold text-slate-900">3D preview</h2>
          <p className="mt-1 text-sm text-slate-500">
            The 3D model has been added to your product gallery. You can rearrange its position in the Shopify Admin.
          </p>
          <div className="mt-4 rounded-3xl border border-slate-100 bg-white/60 p-4">
            {glbProxyUrl ? (
              <ModelViewer src={glbProxyUrl} alt={product.title} orientation={editorOrientation} />
            ) : (
              <div className="flex min-h-[400px] items-center justify-center rounded-xl bg-slate-50 text-slate-500">
                Loading 3D model...
              </div>
            )}
          </div>
        </div>
      )}

      {/* Generation setup panel - shown when user clicks Generate */}
      {showQualitySelector && (
        <div className="glass-panel space-y-5 border border-indigo-200/70 bg-gradient-to-br from-indigo-50/85 via-sky-50/70 to-white">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h3 className="text-lg font-semibold text-slate-900">Generate AR Model</h3>
              <p className="mt-1 text-sm text-slate-500">
                Confirm to start premium 3D generation.
              </p>
            </div>
            <span className="rounded-full border border-indigo-200 bg-white/80 px-3 py-1 text-xs font-semibold text-indigo-700">
              {viewMode === 'multi' ? 'Multi-View Ultra' : 'Single Reference Ultra'}
            </span>
          </div>
          <div className="rounded-2xl border border-indigo-100 bg-white/70 px-4 py-3 space-y-1">
            <p className="text-sm text-slate-600">
              {viewMode === 'multi'
                ? `${selectedImages.filter(Boolean).length || allImages.length} photo(s) — premium multi-view 3D with 4K PBR texture.`
                : 'One front-facing photo — full 360° geometry with 4K PBR texture.'}
            </p>
            <p className="text-xs text-slate-400">
              Estimated time: {viewMode === 'multi' ? '15–35 minutes' : '5–15 minutes'} · Background auto-removed
            </p>
          </div>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={handleGenerateConfirm}
              disabled={
                isGenerating ||
                (viewMode === 'single' && !selectedImages[0] && !allImages[0]) ||
                (viewMode === 'multi' && selectedImages.filter(Boolean).length === 0 && allImages.length === 0)
              }
              className="flex-1 rounded-2xl bg-gradient-to-r from-indigo-500 to-sky-500 px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-indigo-200 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isGenerating ? "Generating..." : "Start Generation"}
            </button>
            <button
              type="button"
              onClick={handleGenerateCancel}
              disabled={isGenerating}
              className="rounded-2xl border border-slate-200 bg-white px-6 py-3 text-sm font-semibold text-slate-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Quota cap modal */}
      <QuotaCapModal
        isOpen={showQuotaModal}
        onClose={() => setShowQuotaModal(false)}
      />

      {/* Bottom detail tabs: Generation Settings | Edit Asset */}
      <div className="glass-panel">
        <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid #e5e7eb', marginBottom: 16 }}>
          <button
            onClick={() => setActiveDetailTab('generation')}
            style={{
              padding: '8px 18px', fontSize: 13, fontWeight: 600, border: 'none', background: 'none',
              borderBottom: activeDetailTab === 'generation' ? '2px solid #6366f1' : '2px solid transparent',
              color: activeDetailTab === 'generation' ? '#6366f1' : '#888',
              cursor: 'pointer',
            }}
          >
            Generation Settings
          </button>
          {product?.asset?.status === 'ready' && product.asset.glb_url && (
            <button
              onClick={() => setActiveDetailTab('edit-asset')}
              style={{
                padding: '8px 18px', fontSize: 13, fontWeight: 600, border: 'none', background: 'none',
                borderBottom: activeDetailTab === 'edit-asset' ? '2px solid #6366f1' : '2px solid transparent',
                color: activeDetailTab === 'edit-asset' ? '#6366f1' : '#888',
                cursor: 'pointer',
              }}
            >
              Edit Asset
            </button>
          )}
        </div>

        {activeDetailTab === 'generation' && (
          product?.asset ? (
            <AssetSettingsForm asset={product.asset} onSubmit={updateSettings} />
          ) : (
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Generation Settings</h2>
              <p className="mt-2 text-sm text-slate-500">
                Generate an AR model first to configure placement and scale settings.
              </p>
              {!showQualitySelector && !isGenerating && (
                <button
                  type="button"
                  onClick={handleGenerateClick}
                  disabled={isGenerating}
                  className="mt-4 rounded-2xl bg-gradient-to-r from-indigo-500 to-sky-500 px-6 py-3 text-sm font-semibold text-white shadow-xl shadow-indigo-200 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  Generate AR model
                </button>
              )}
            </div>
          )
        )}

        {activeDetailTab === 'edit-asset' && product?.asset && (
          <AssetEditor
            productId={product.id}
            assetId={product.asset.id}
            glbProxyUrl={glbProxyUrl}
            onPublished={async (_, orientationOverride) => {
              if (orientationOverride !== undefined) {
                setEditorOrientation(orientationOverride ?? null);
              }
              setGlbRefreshKey((k) => k + 1);
              await refetch(true);
            }}
          />
        )}
      </div>
    </div>
  );
}
